import {
  Card,
  CardActionArea,
  CardContent,
  Menu,
  Box,
  Typography,
} from '@mui/material';
import useElementResize from 'hooks/useElementResize';
import React, { FC, cloneElement, useRef, useState } from 'react';
import RenameMenuItem from 'components/MenuDropdown/RenameMenuItem';
import RemoveMenuItem from 'components/MenuDropdown/RemoveMenuItem';
import NameModal from 'components/Dialogs/NameModal';
import AlertDialog from 'components/Dialogs/AlertDialog';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from 'components/Base/Card/CardHeader';
import UpdateLinkMenuItem from 'components/MenuDropdown/UpdateLinkMenuItem';
import LinkModal from 'components/Dialogs/LinkModal';
import PinMenuItem from 'components/MenuDropdown/PinMenuItem';

type TActiveToolCardProps = {
  onClick: () => void;
  children: React.ReactElement;
  name: string;
  link?: string;
  onRenameTool?: (name: string) => Promise<void>;
  onRemoveTool?: () => Promise<void>;
  removeLoading?: boolean;
  onUpdateLink?: (link: string) => Promise<void>;
  pinProps?: {
    inShortcuts: boolean;
    onClick: () => void;
  };
};

export type TActiveToolCardChildrenProps = {
  contentWidth?: number;
  contentHeight?: number;
};

const ActiveToolCard: FC<TActiveToolCardProps> = ({
  onClick,
  children,
  name,
  link,
  onRenameTool,
  onRemoveTool,
  onUpdateLink,
  removeLoading,
  pinProps,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [openRenameModal, setOpenRenameModal] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [openUpdateLinkModal, setOpenUpdateLinkModal] = useState(false);

  const cardContentRef = useRef<HTMLDivElement>(null);
  const { width, height } = useElementResize({
    ref: cardContentRef,
  });
  const childrenWithProps = cloneElement(children, {
    contentWidth: width,
    contentHeight: height,
  } as TActiveToolCardChildrenProps);

  const onOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const showActionsMenu = Boolean(onRenameTool || onRemoveTool);

  return (
    <Grid
      xs={12}
      sm={6}
      md={4}
      lg={3}
      sx={{ height: '50%', minHeight: 210, minWidth: 300 }}
      padding={1}
    >
      <Card
        sx={{
          height: 1,
          borderRadius: 3,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <CardActionArea
          onClick={onClick}
          sx={{
            flexGrow: 1,
            height: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '0.9375rem',
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: showActionsMenu ? 'calc(100% - 40px)' : '100%',
              }}
            >
              {name}
            </Typography>
            {showActionsMenu && (
              <Box
                onClick={e => e.stopPropagation()}
                sx={{ flexShrink: 0 }}
              >
                <CardHeader
                  sx={{
                    p: 0,
                    '& .MuiCardHeader-action': {
                      m: 0,
                    },
                  }}
                  hasaction={{ onActionClick: onOpenMenu }}
                />
              </Box>
            )}
          </Box>
          <CardContent
            sx={{
              width: 1,
              flexGrow: 1,
              overflow: 'hidden',
              padding: 0,
              backgroundColor: 'background.default',
            }}
            ref={cardContentRef}
          >
            {childrenWithProps}
          </CardContent>
        </CardActionArea>
      </Card>
      {showActionsMenu && (
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
          onBlur={() => setMenuAnchorEl(null)}
        >
          {onRenameTool && (
            <RenameMenuItem onClick={() => setOpenRenameModal(true)} />
          )}
          {onUpdateLink && (
            <UpdateLinkMenuItem
              onClick={() => {
                setOpenUpdateLinkModal(true);
              }}
            />
          )}
          {pinProps && (
            <PinMenuItem
              inShortcuts={pinProps.inShortcuts}
              onClick={pinProps.onClick}
            />
          )}
          {onRemoveTool && (
            <RemoveMenuItem onClick={() => setOpenRemoveModal(true)} />
          )}
        </Menu>
      )}
      {openRenameModal && onRenameTool && (
        <NameModal
          modalTitle="Rename"
          open={openRenameModal}
          setOpen={setOpenRenameModal}
          initialName={name}
          onSubmitModal={value => onRenameTool(value)}
        />
      )}
      {openUpdateLinkModal && onUpdateLink && (
        <LinkModal
          modalTitle="Update Link"
          open={openUpdateLinkModal}
          setOpen={setOpenUpdateLinkModal}
          initialLink={link}
          onSubmitModal={value => onUpdateLink(value)}
        />
      )}
      {openRemoveModal && onRemoveTool && (
        <AlertDialog
          open={openRemoveModal}
          title="Remove Tool"
          text="Are you sure you want to permanently remove this tool? This action cannot be undone."
          onCancel={() => {
            setOpenRemoveModal(false);
          }}
          onConfirm={onRemoveTool}
          submitLoading={removeLoading}
          submitDisabled={removeLoading}
        />
      )}
    </Grid>
  );
};

export default ActiveToolCard;
